// Capability hooks. Centralises "what is this user allowed to do" so UI
// gates don't reach into Clerk's `has()` directly. Backed by Clerk
// permissions (see PERMISSIONS in lib/roles.ts) — add a new capability
// here when introducing a new permission slug, and route every call
// site through the predicate so the role/permission mapping stays in
// one place.
//
// Returns `loaded` so consumers can render a neutral state until Clerk
// resolves; treating loading as "not allowed" briefly is preferable to
// flashing a CTA the user can't actually use.

import { useAuth } from "@/lib/auth";
import { PERMISSIONS } from "@/lib/roles";

interface Capabilities {
  loaded: boolean;
  canManageBilling: boolean;
  canManageMembers: boolean;
  canRenameOrg: boolean;
  canDeleteOrg: boolean;
  canViewAuditLog: boolean;
}

const NONE: Capabilities = {
  loaded: false,
  canManageBilling: false,
  canManageMembers: false,
  canRenameOrg: false,
  canDeleteOrg: false,
  canViewAuditLog: false,
};

export function useCapabilities(): Capabilities {
  const { isLoaded, has } = useAuth();
  if (!isLoaded || !has) return NONE;
  return {
    loaded: true,
    canManageBilling: has({ permission: PERMISSIONS.billingManage }),
    canManageMembers: has({ permission: PERMISSIONS.membershipsManage }),
    canRenameOrg: has({ permission: PERMISSIONS.profileManage }),
    canDeleteOrg: has({ permission: PERMISSIONS.profileDelete }),
    canViewAuditLog: has({ permission: PERMISSIONS.auditLogRead }),
  };
}
